/**
 * Medea leave lane E policy calculations.
 *
 * These are deliberately pure, integer-unit rules. A controlled leave RPC or
 * payroll writer owns persistence, authorisation, audit rows and provider
 * transport; callers must not use these helpers as permission to write a
 * protected leave or payroll table directly.
 */

export const MEDEA_LEAVE_UNITS_PER_HOUR = 10_000 as const;
export const MEDEA_LEAVE_UNITS_PER_DAY = 76_000 as const;
export const MEDEA_GS_ADO_UNPAID_ABSENCE_DEDUCTION_UNITS = 4_000 as const;
export const MEDEA_ANNUAL_LEAVE_PROTECTED_MINIMUM_UNITS = 1_520_000 as const;

export type MedeaAgreementCohort = 'GENERAL_STAFF' | 'NURSES';

export type MedeaOccasionCapResult = {
  readonly withinOrdinaryCap: boolean;
  readonly approvalWarning: string | null;
  readonly requiresCeoDiscretionRecord: boolean;
};

/**
 * Checks an occasion as it is submitted or approved. A request over the EA
 * cap remains reviewable, but an approval is only valid when the CEO
 * discretion has been expressly recorded; silently clipping leave would lose
 * the worker's requested absence and would obscure the decision.
 */
export function assessMedeaCompassionateBereavementOccasion(args: {
  cohort: MedeaAgreementCohort;
  compassionateDays: number;
  bereavementDays: number;
  ceoDiscretionRecorded?: boolean;
}): MedeaOccasionCapResult {
  const compassionate = Math.max(0, args.compassionateDays);
  const bereavement = Math.max(0, args.bereavementDays);
  const exceedsKindCap = compassionate > 2 || bereavement > 3;
  const exceedsCombinedGsCap = args.cohort === 'GENERAL_STAFF' && compassionate + bereavement > 5;
  const exceeds = exceedsKindCap || exceedsCombinedGsCap;
  if (!exceeds) {
    return { withinOrdinaryCap: true, approvalWarning: null, requiresCeoDiscretionRecord: false };
  }
  const warning = exceedsCombinedGsCap
    ? 'This occasion exceeds the General Staff combined five-day compassionate and bereavement cap.'
    : compassionate > 2
      ? 'This occasion exceeds the two-day compassionate leave cap.'
      : 'This occasion exceeds the three-day bereavement leave cap.';
  return {
    withinOrdinaryCap: Boolean(args.ceoDiscretionRecorded),
    approvalWarning: warning,
    requiresCeoDiscretionRecord: !args.ceoDiscretionRecorded,
  };
}

/**
 * NES s106A: 10 paid FDV days in each 12-month period of employment (renewed
 * at each employment anniversary, not accumulating). The caller supplies the
 * days already taken in the current period.
 */
export function fdvLeaveRemainingUnits(args: {
  alreadyTakenUnits: number;
  requestedUnits: number;
  ordinaryDayUnits?: number;
}): { remainingBeforeRequestUnits: number; exceedsEntitlement: boolean } {
  const annualEntitlement = 10 * (args.ordinaryDayUnits ?? MEDEA_LEAVE_UNITS_PER_DAY);
  const alreadyTaken = Math.max(0, Math.trunc(args.alreadyTakenUnits));
  const requested = Math.max(0, Math.trunc(args.requestedUnits));
  return {
    remainingBeforeRequestUnits: Math.max(0, annualEntitlement - alreadyTaken),
    exceedsEntitlement: alreadyTaken + requested > annualEntitlement,
  };
}

/** GS special leave is a separate five-day, non-accumulating 1 July pool. */
export function gsSpecialLeaveRemainingUnits(args: {
  alreadyTakenUnits: number;
  requestedUnits: number;
  ordinaryDayUnits?: number;
}): { remainingBeforeRequestUnits: number; exceedsEntitlement: boolean } {
  const annualEntitlement = 5 * (args.ordinaryDayUnits ?? MEDEA_LEAVE_UNITS_PER_DAY);
  const alreadyTaken = Math.max(0, Math.trunc(args.alreadyTakenUnits));
  const requested = Math.max(0, Math.trunc(args.requestedUnits));
  return {
    remainingBeforeRequestUnits: Math.max(0, annualEntitlement - alreadyTaken),
    exceedsEntitlement: alreadyTaken + requested > annualEntitlement,
  };
}

/** Jury pay is limited to the first ten days; emergency activity is unpaid. */
export function juryServicePayableUnits(args: {
  completedJuryDays: number;
  requestedUnits: number;
  ordinaryDayUnits?: number;
}): number {
  const remainingDays = Math.max(0, 10 - Math.max(0, Math.trunc(args.completedJuryDays)));
  return Math.min(
    Math.max(0, Math.trunc(args.requestedUnits)),
    remainingDays * (args.ordinaryDayUnits ?? MEDEA_LEAVE_UNITS_PER_DAY),
  );
}

export function ceremonialLeaveExceedsAnnualCap(args: {
  completedWorkingDays: number;
  requestedWorkingDays: number;
}): boolean {
  return (
    Math.max(0, Math.trunc(args.completedWorkingDays)) +
      Math.max(0, Math.trunc(args.requestedWorkingDays)) >
    10
  );
}

/** The GS additional-carer pool expires at the end of 20 June. */
export function gsExtraCarerExpiryDate(grantedOn: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(grantedOn);
  if (!match) throw new Error('invalid_granted_on_date');
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) throw new Error('invalid_granted_on_date');
  return `${year + (month > 6 || (month === 6 && day > 20) ? 1 : 0)}-06-20`;
}

/** Exact TOIL credit. A non-representable result is refused rather than rounded. */
export function accrueToilUnits(args: {
  overtimeUnits: number;
  multiplierBasisPoints: number;
}): number {
  const numerator =
    Math.max(0, Math.trunc(args.overtimeUnits)) *
    Math.max(0, Math.trunc(args.multiplierBasisPoints));
  if (!Number.isSafeInteger(numerator) || numerator % 10_000 !== 0) {
    throw new Error('toil_credit_not_representable_in_units_1e4');
  }
  return numerator / 10_000;
}

export function adoUnpaidAbsenceDeductionUnits(unpaidAbsenceDays: number): number {
  // eslint-disable-next-line statura/no-floating-point-money -- both operands are integer units_1e4, not currency.
  return Math.max(0, Math.trunc(unpaidAbsenceDays)) * MEDEA_GS_ADO_UNPAID_ABSENCE_DEDUCTION_UNITS;
}

export function annualLeaveCashOutPermitted(args: {
  availableUnits: number;
  cashOutUnits: number;
  protectedMinimumUnits?: number;
  writtenAgreementRecorded: boolean;
}): boolean {
  return (
    args.writtenAgreementRecorded &&
    Number.isInteger(args.availableUnits) &&
    Number.isInteger(args.cashOutUnits) &&
    args.cashOutUnits > 0 &&
    args.availableUnits - args.cashOutUnits >=
      (args.protectedMinimumUnits ?? MEDEA_ANNUAL_LEAVE_PROTECTED_MINIMUM_UNITS)
  );
}

/**
 * NES s130: leave is generally not taken or accrued while a worker is absent
 * and receiving workers' compensation, except as the state compensation law
 * permits. That is a provider determination, not a system refusal: Statura
 * recommends, and paid leave in a compensable period needs a recorded
 * provider authority (reason, actor, time) for any cohort and leave type.
 */
export function workersCompLeaveDecision(args: {
  compensable: boolean;
  providerAuthorityRecorded?: boolean;
}): 'ALLOW' | 'REQUIRE_PROVIDER_AUTHORITY' {
  if (!args.compensable || args.providerAuthorityRecorded) return 'ALLOW';
  return 'REQUIRE_PROVIDER_AUTHORITY';
}

export type MedeaEmployerPaidParentalPaymentKind =
  | 'GENERAL_STAFF_PARENTAL_PRE_BIRTH'
  | 'GENERAL_STAFF_PARENTAL_POST_RETURN'
  | 'GENERAL_STAFF_PARTNER_PRE_BIRTH'
  | 'NURSES_BIRTH_PARENT'
  | 'NURSES_NON_BIRTH_PARTNER';

export type MedeaNursesBirthParentPaidParentalElection = 'FULL_PAY' | 'HALF_PAY';

export function nursesBirthParentPaidParentalElection(
  election: MedeaNursesBirthParentPaidParentalElection,
): { readonly weeks: number; readonly rateFractionBasisPoints: number } {
  return election === 'HALF_PAY'
    ? { weeks: 28, rateFractionBasisPoints: 5_000 }
    : { weeks: 14, rateFractionBasisPoints: 10_000 };
}

/** Provider-paid parental/partner entitlement is an earnings item, never a Xero LeaveType. */
export const MEDEA_EMPLOYER_PAID_PARENTAL_PAYMENT_RULES: Readonly<
  Record<
    MedeaEmployerPaidParentalPaymentKind,
    {
      readonly weeks: number;
      readonly rateFractionBasisPoints: number;
      readonly timing: string;
      readonly earningsCategory: 'EMPLOYER_PAID_PARENTAL' | 'EMPLOYER_PAID_PARTNER';
      readonly superable: boolean;
    }
  >
> = Object.freeze({
  GENERAL_STAFF_PARENTAL_PRE_BIRTH: {
    weeks: 4,
    rateFractionBasisPoints: 10_000,
    timing: 'before birth',
    earningsCategory: 'EMPLOYER_PAID_PARENTAL',
    superable: true,
  },
  GENERAL_STAFF_PARENTAL_POST_RETURN: {
    weeks: 3,
    rateFractionBasisPoints: 10_000,
    timing: 'first full payroll after return',
    earningsCategory: 'EMPLOYER_PAID_PARENTAL',
    superable: true,
  },
  GENERAL_STAFF_PARTNER_PRE_BIRTH: {
    weeks: 2,
    rateFractionBasisPoints: 10_000,
    timing: 'before birth',
    earningsCategory: 'EMPLOYER_PAID_PARTNER',
    superable: true,
  },
  NURSES_BIRTH_PARENT: {
    weeks: 14,
    rateFractionBasisPoints: 10_000,
    timing: 'chosen full-pay election; alternatively 28 weeks at half pay',
    earningsCategory: 'EMPLOYER_PAID_PARENTAL',
    superable: true,
  },
  NURSES_NON_BIRTH_PARTNER: {
    weeks: 1,
    rateFractionBasisPoints: 10_000,
    timing: 'before or around birth as approved',
    earningsCategory: 'EMPLOYER_PAID_PARTNER',
    superable: true,
  },
});
